import React, { useState, useEffect } from 'react';
import { TrendingUp, Users, Target, Zap, Check, Star, Award, Clock } from 'lucide-react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserMealPlans, selectUserMealPlans, selectUserMealPlansStatus } from '../redux/userSlice';

export default function PopularMealPlans({ mealPlansData = null }) {
  const [hoveredPlan, setHoveredPlan] = useState(null);
  const [mealPlans, setMealPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const dispatch = useDispatch();
  const reduxMealPlans = useSelector(selectUserMealPlans);
  const mealPlansStatus = useSelector(selectUserMealPlansStatus);

  const mealPlansSource = mealPlansData !== null ? mealPlansData : reduxMealPlans;

  useEffect(() => {
    dispatch(fetchUserMealPlans());
  }, [dispatch]);

  useEffect(() => {
    if (mealPlansSource && mealPlansSource.length > 0) {
      setMealPlans(mapPlans(mealPlansSource));
      setLoading(false);
    } else if (mealPlansStatus === 'succeeded' || mealPlansStatus === 'failed') {
      setLoading(false);
    }
  }, [mealPlansSource, mealPlansStatus]);

  // Map API data to UI structure
  const mapPlans = (rawPlans) => {
    return rawPlans.slice(0, 3).map((plan, index) => {
      const themes = [
        { color: "from-emerald-600 to-green-700", icon: <TrendingUp className="w-6 h-6" />, badge: "Most Popular" },
        { color: "from-teal-600 to-emerald-700", icon: <Target className="w-6 h-6" />, badge: "Best Results" },
        { color: "from-green-700 to-emerald-800", icon: <Zap className="w-6 h-6" />, badge: "Premium Choice" }
      ];
      const theme = themes[index % themes.length];
      return {
        id: plan._id,
        name: plan.mealPlanName,
        description: plan.description || "A scientifically designed meal plan tailored to your health and fitness goals.",
        calories: "Customizable",
        protein: "High",
        meals: "5-6 meals/day",
        duration: "Monthly",
        badge: theme.badge,
        color: theme.color,
        icon: theme.icon,
        image: plan.image && plan.image.length > 0
          ? plan.image[0]
          : "https://images.unsplash.com/photo-1532550907401-a500c9a57435?w=800&q=80",
        benefits: ["Nutritionist Approved", "Fresh Ingredients", "Flexible Menu", "Doorstep Delivery"]
      };
    });
  };

  // ── Skeleton Loading State — replaces dark full-page grey screen ────────────
  if (loading) {
    return (
      <div className="bg-gradient-to-br from-green-950 via-emerald-900 to-green-950 py-20 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="h-8 w-48 bg-white/10 rounded-full mx-auto mb-4 animate-pulse"></div>
            <div className="h-12 w-80 bg-white/10 rounded-xl mx-auto mb-4 animate-pulse"></div>
            <div className="h-6 w-64 bg-white/10 rounded-lg mx-auto animate-pulse"></div>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white/5 rounded-3xl overflow-hidden border border-white/10 animate-pulse">
                <div className="h-48 bg-white/10"></div>
                <div className="p-6 space-y-4">
                  <div className="h-6 w-3/4 bg-white/10 rounded-lg"></div>
                  <div className="h-4 w-full bg-white/10 rounded"></div>
                  <div className="grid grid-cols-2 gap-3">
                    {[1, 2, 3, 4].map(j => <div key={j} className="h-16 bg-white/10 rounded-xl"></div>)}
                  </div>
                  <div className="h-12 bg-white/10 rounded-2xl"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (mealPlans.length === 0) return null;

  return (
    // Fixed: min-h-screen removed (was forcing full-page dark block)
    // Fixed: background animated blobs changed from fixed→absolute so they don't cover viewport
    <div className="bg-gradient-to-br from-green-950 via-emerald-900 to-green-950 py-20 px-8 relative overflow-hidden">
      {/* Animated Background Elements — Fixed: absolute instead of fixed */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-emerald-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-green-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header — Fixed: h1 → h2 (page already has an h1) */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-emerald-500/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
            <Award className="w-5 h-5 text-emerald-400" />
            <span className="text-emerald-300 text-sm font-medium">Premium Nutrition Plans</span>
          </div>
          <h2 className="text-5xl font-bold text-white mb-4 bg-clip-text text-transparent bg-gradient-to-r from-emerald-300 to-green-300">
            Popular Meal Plans
          </h2>
          <p className="text-xl text-emerald-100/80 max-w-2xl mx-auto">
            Transform your body with our scientifically designed meal plans tailored to your fitness goals
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {mealPlans.map((plan, index) => (
            <div
              key={plan.id}
              onMouseEnter={() => setHoveredPlan(plan.id)}
              onMouseLeave={() => setHoveredPlan(null)}
              className="relative group"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Glow Effect */}
              <div className={`absolute inset-0 bg-gradient-to-r ${plan.color} rounded-3xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-500`}></div>

              {/* Card */}
              <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg rounded-3xl overflow-hidden border border-emerald-500/20 hover:border-emerald-400/40 transition-all duration-500 transform hover:-translate-y-2">

                {/* Badge */}
                <div className="absolute top-6 right-6 z-20">
                  <div className={`bg-gradient-to-r ${plan.color} text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg`}>
                    {plan.badge}
                  </div>
                </div>

                {/* Image Section */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={plan.image}
                    alt={plan.name}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-4 left-6 bg-white/20 backdrop-blur-md rounded-2xl p-3 text-white">
                    {plan.icon}
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                  <p className="text-emerald-100/70 text-sm mb-6 min-h-[40px] line-clamp-2">{plan.description}</p>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    <div className="bg-white/5 rounded-xl p-3">
                      <p className="text-emerald-300 text-xs mb-1">Calories</p>
                      <p className="text-white font-semibold">{plan.calories}</p>
                    </div>
                    <div className="bg-white/5 rounded-xl p-3">
                      <p className="text-emerald-300 text-xs mb-1">Protein</p>
                      <p className="text-white font-semibold">{plan.protein}</p>
                    </div>
                    <div className="bg-white/5 rounded-xl p-3">
                      <p className="text-emerald-300 text-xs mb-1">Schedule</p>
                      <p className="text-white font-semibold">{plan.meals}</p>
                    </div>
                    <div className="bg-white/5 rounded-xl p-3">
                      <p className="text-emerald-300 text-xs mb-1">Plan Duration</p>
                      <p className="text-white font-semibold">{plan.duration}</p>
                    </div>
                  </div>

                  {/* Benefits — revealed on hover */}
                  <div className={`space-y-2 mb-6 transition-all duration-500 ${hoveredPlan === plan.id ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                    {plan.benefits.map((benefit, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                        <span className="text-emerald-100/80 text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>

                  {/* Price and CTA */}
                  <div className="flex items-end justify-between">
                    <div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-xl font-bold text-white">View Details</span>
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-3 h-3 fill-emerald-400 text-emerald-400" />
                        ))}
                        <span className="text-emerald-300/70 text-xs ml-1">4.9 (500+)</span>
                      </div>
                    </div>
                    <button className={`bg-gradient-to-r ${plan.color} text-white font-semibold px-6 py-3 rounded-2xl hover:shadow-2xl hover:shadow-emerald-500/25 transform hover:scale-105 transition-all duration-300`}>
                      Select Plan
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA Section */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-3 bg-emerald-500/10 backdrop-blur-sm rounded-2xl px-6 py-4 border border-emerald-500/20">
            <Clock className="w-5 h-5 text-emerald-400" />
            <p className="text-emerald-100">
              <span className="font-semibold text-white">Limited Time:</span> Get 20% off your first month with code TRANSFORM20
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
